"use client";

import { getGizmoSession } from "@/lib/gizmoSession";
import {
  Box,
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Badge,
  SimpleGrid,
  Alert,
} from "@mantine/core";
import { IconArrowRight, IconLock } from "@tabler/icons-react";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { PRICING } from "@/constants/pricing";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import firebase_app from "@/firebase/config";
import { FaceScanOverlay } from "@/app/[locale]/components/FaceScanOverlay";
import { trackCheckoutStart, trackCtaClick, trackGizmo } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import DrapeKitCard from "@/components/DrapeKitCard";
import { getStoredLeadEmail } from "@/components/CheckoutEmailStep";
import { getPriceBucket, bucketEventMeta } from "@/lib/priceExperiment";
import { useTranslations } from "next-intl";
import { STYLE_PROFILE_V1 } from "@/lib/styleProfileFlag";
import { FREE_DRAPES_V1 } from "@/lib/freeDrapesFlag";

const shimmerStyles = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes pulse {
  0%, 100% { opacity: 1; border-color: #F87171; }
  50% { opacity: 0.85; border-color: #EF4444; }
}
`;

type AnalysisResult = {
  season: string;
  subSeason: string;
  confidence: number;
  secondarySeason: string;
  secondaryConfidence: number;
  goodColors: { name: string; hex: string }[];
  avoidColor: { name: string; hex: string; reason: string };
  undertone: string;
  contrast: string;
  depth?: string;
};

type FlowState = "analyzing" | "results" | "error";

const ANALYSIS_DURATION = 9500;

// Dimension styling - color-aware backgrounds
const UNDERTONE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  warm: { bg: "#FEF3E2", text: "#92400E", label: "Warm" },
  cool: { bg: "#EFF6FF", text: "#1E40AF", label: "Cool" },
  neutral: { bg: "#F5F5F4", text: "#57534E", label: "Neutral" },
};

const CONTRAST_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  high: { bg: "#1F2937", text: "#F9FAFB", label: "High" },
  medium: { bg: "#6B7280", text: "#F9FAFB", label: "Medium" },
  low: { bg: "#D1D5DB", text: "#374151", label: "Low" },
};

const DEPTH_LABELS: Record<string, string> = {
  light: "Light",
  medium: "Medium",
  "medium-deep": "Medium-Deep",
  deep: "Deep",
};

export default function Content() {
  const t = useTranslations("aiColorAnalysis.result");
  const tFreeDrapes = useTranslations("freeDrapes");
  const router = useRouter();
  const searchParams = useSearchParams();

  const [flowState, setFlowState] = useState<FlowState>("analyzing");
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  // Draping preview state
  const [drapingPreviews, setDrapingPreviews] = useState<(string | null)[]>([]);
  const [drapingColors, setDrapingColors] = useState<{ name: string; hex: string }[]>([]);

  // Free digital drapes decline panel (see PLAN-SUMMER-2026.md §5.5) — gated
  // to decline signals only (Stripe cancel return, or dwelling without
  // buying) so paying customers never see the free-path exit.
  const [showFreeDrapes, setShowFreeDrapes] = useState(false);
  const checkoutClickedRef = useRef(false);
  const fdCancelledViewFired = useRef(false);

  // Checkout state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [priceDisplay, setPriceDisplay] = useState<string | null>(null);
  const checkoutInitiated = useRef(false);
  const spOfferViewFired = useRef(false);
  const prePaymentSessionIdRef = useRef<string | null>(null);

  // Preview price shown on the results CTA — purely display, resolved
  // server-side from the same bucket checkout charges (see geo-pricing route).
  useEffect(() => {
    fetch(`/api/geo-pricing?bucket=${getPriceBucket().id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.priceDisplay && setPriceDisplay(d.priceDisplay))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const image = sessionStorage.getItem("colorAnalysisImage");

    if (!image) {
      router.push("/ai-color-analysis");
      return;
    }

    // Cached result/draping/checkout only apply to the image they were
    // generated from. If a new photo was uploaded, the stored image won't
    // match — discard the stale cache so we run a fresh analysis and create a
    // new checkout session instead of reusing a completed Stripe session.
    const cachedResultImage = sessionStorage.getItem("cachedResultImage");
    if (cachedResultImage !== image) {
      sessionStorage.removeItem("preliminaryResult");
      sessionStorage.removeItem("drapingPreviews");
      sessionStorage.removeItem("drapingColors");
      sessionStorage.removeItem("preparedCheckoutUrl");
      sessionStorage.removeItem("cachedResultImage");
      sessionStorage.removeItem("qaGuidedVerdict");
    }

    const existingResult = sessionStorage.getItem("preliminaryResult");
    const existingDrapingPreviews = sessionStorage.getItem("drapingPreviews");
    const existingDrapingColors = sessionStorage.getItem("drapingColors");

    setUploadedImage(image);

    if (existingResult) {
      try {
        const parsed = JSON.parse(existingResult);
        setAnalysisResult(parsed);
        setFlowState("results");

        if (existingDrapingPreviews) {
          try {
            const parsedPreviews = JSON.parse(existingDrapingPreviews);
            if (Array.isArray(parsedPreviews) && parsedPreviews.length > 0) {
              setDrapingPreviews(parsedPreviews);
              if (existingDrapingColors) {
                setDrapingColors(JSON.parse(existingDrapingColors));
              }
            }
          } catch {}
        }


        return;
      } catch {}
    }

    runAnalysis(image);
  }, [router]);

  useEffect(() => {
    if (STYLE_PROFILE_V1 && analysisResult && !spOfferViewFired.current) {
      spOfferViewFired.current = true;
      trackCtaClick("sp_offer_view");
    }
  }, [analysisResult]);

  // Guided-vs-AI agreement datapoint: if this user ran the free Guided
  // Color Analysis first, compare its verdict with the AI's in ONE event
  // (no cross-event joins needed). This is the ground-truth feed for
  // calibrating lib/seasonFromSampledColors.
  useEffect(() => {
    if (!analysisResult) return;
    const stored = sessionStorage.getItem("qaGuidedVerdict");
    if (!stored) return;
    try {
      const guided = JSON.parse(stored);
      trackGizmo("qa_ai_verdict", {
        ai_season: analysisResult.season,
        ai_sub_season: analysisResult.subSeason,
        guided_season: guided.season,
        guided_sub_season: guided.subSeason,
        season_match: guided.season === analysisResult.season,
        exact_match:
          guided.season === analysisResult.season &&
          guided.subSeason === analysisResult.subSeason,
      });
    } catch {}
    sessionStorage.removeItem("qaGuidedVerdict");
  }, [analysisResult]);

  // Free digital drapes trigger (a): Stripe cancel return. Only fires if the
  // checkout cancel flow actually lands back on this page with ?cancelled=true.
  useEffect(() => {
    if (!FREE_DRAPES_V1 || fdCancelledViewFired.current) return;
    if (searchParams.get("cancelled") === "true") {
      fdCancelledViewFired.current = true;
      setShowFreeDrapes(true);
      trackCtaClick("fd_result_view_cancelled");
    }
  }, [searchParams]);

  const prepareCheckout = async (
    result: AnalysisResult,
    customerEmail: string | null
  ): Promise<string> => {
    // Phase A: ensure the photo is uploaded and the PENDING_PAYMENT doc
    // exists. Retries reuse the same photo and pre-payment document
    // if only the Stripe session request failed.
    if (!prePaymentSessionIdRef.current) {
      const imageData = sessionStorage.getItem("colorAnalysisImage");
      if (!imageData) throw new Error("Missing analysis photo");

      const sessionId = `analysis_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const storage = getStorage(firebase_app);
      const firestore = getFirestore(firebase_app);

      const response = await fetch(imageData);
      const blob = await response.blob();
      const imageRef = ref(storage, `generatedAnalyses/${sessionId}/image_1.png`);
      await uploadBytes(imageRef, blob);

      const analysisRef = doc(firestore, "generatedColorAnalyses", sessionId);
      await setDoc(analysisRef, {
        analysisId: sessionId,
        selectedTier: "full",
        createdAt: new Date(),
        status: "PENDING_PAYMENT",
        photoCount: 1,
        preliminaryResult: result,
      });

      prePaymentSessionIdRef.current = sessionId;
    }

    // Phase B: create the Stripe checkout session.
    const checkoutResponse = await fetch("/api/stripe/createStripeCheckoutPage", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gizmoSession: getGizmoSession(),
        type: "color-analysis",
        includeDraping: true,
        prePaymentSessionId: prePaymentSessionIdRef.current,
        bucketId: getPriceBucket().id,
        ...(customerEmail && { customerEmail }),
        // Cancel returns here (route allowlists this path) so the balked
        // buyer lands back on their preview, where the free-drapes decline
        // offer shows via the cancelled=true trigger.
        cancelPagePath: window.location.pathname,
      }),
    });

    const checkoutData = await checkoutResponse.json();
    if (!checkoutResponse.ok || !checkoutData.url) throw new Error("Checkout session creation failed");
    // Never fall back to an expired checkout URL after a failed request.
    return checkoutData.url;
  };

  const proceedToCheckout = async () => {
    if (checkoutInitiated.current) return;
    checkoutInitiated.current = true;
    setCheckoutLoading(true);
    setCheckoutError(null);
    const startedAt = Date.now();
    try {
      const result = analysisResult;
      if (!result) throw new Error("Missing analysis result");
      const email = getStoredLeadEmail();
      const url = await prepareCheckout(result, email);

      // Lead capture for abandoned-checkout recovery. keepalive so the
      // request survives the redirect; never block checkout on failure.
      if (email) {
        void fetch("/api/capture-email", {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            source: "checkout_intent",
            prePaymentSessionId: prePaymentSessionIdRef.current,
            bucketId: getPriceBucket().id,
            checkoutPath: window.location.pathname,
          }),
        }).catch(() => {});
      }

      trackGizmo("checkout_redirect", { tool: "color-analysis", flow: "direct_stripe_v1", elapsed_ms: Date.now() - startedAt });
      window.location.href = url;
    } catch (error) {
      console.error("Error opening checkout:", error);
      checkoutInitiated.current = false;
      setCheckoutLoading(false);
      setCheckoutError("We couldn't open checkout. Please try again. You haven't been charged.");
      trackGizmo("checkout_error", { tool: "color-analysis", flow: "direct_stripe_v1" });
    }
  };

  const handleCheckout = () => {
    checkoutClickedRef.current = true;
    if (checkoutInitiated.current) return;
    if (STYLE_PROFILE_V1) trackCtaClick("sp_checkout_start");
    trackCheckoutStart("color-analysis", { ...bucketEventMeta(getPriceBucket()), flow: "direct_stripe_v1" });
    void proceedToCheckout();
  };

  const handleFreeEscape = () => {
    trackCtaClick("sp_free_escape");
    router.push("/ai-color-analysis/self-assessment");
  };

  const runAnalysis = async (imageData: string) => {
    const startTime = Date.now();

    try {
      const response = await fetch("/api/preliminary-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Analysis failed");
      }


      const elapsed = Date.now() - startTime;
      if (elapsed < ANALYSIS_DURATION) {
        await new Promise((resolve) => setTimeout(resolve, ANALYSIS_DURATION - elapsed));
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      setAnalysisResult(data.result);
      sessionStorage.setItem("preliminaryResult", JSON.stringify(data.result));
      sessionStorage.setItem("cachedResultImage", imageData);
      setFlowState("results");
    } catch (error) {
      console.error("Analysis error:", error);
      setErrorMessage(error instanceof Error ? error.message : "Analysis failed");
      setFlowState("error");
    }
  };

  // ============================================
  // RENDER: ANALYZING
  // ============================================

  if (flowState === "analyzing") {
    return (
      <Box py={{ base: "1.5rem", md: "2rem" }} style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Container size="xs">
          <Stack gap="lg" align="center">
            {uploadedImage && (
              <Box style={{ position: "relative", borderRadius: 24, overflow: "hidden", maxWidth: 380, width: "100%", boxShadow: "0 25px 80px rgba(0,0,0,0.2)" }}>
                <FaceScanOverlay imageSrc={uploadedImage} isAnalyzing={true} currentStepIndex={0} />
              </Box>
            )}
          </Stack>
        </Container>
      </Box>
    );
  }

  // ============================================
  // RENDER: ERROR
  // ============================================

  if (flowState === "error") {
    return (
      <Box py={{ base: "3rem", md: "5rem" }}>
        <Container size="sm">
          <Stack gap="xl" align="center">
            <Paper p="xl" radius="lg" style={{ backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }} w="100%">
              <Stack gap="md" align="center">
                <Title order={3} style={{ color: "#991B1B" }}>{t("errorTitle")}</Title>
                <Text ta="center" style={{ color: "#7F1D1D" }}>{errorMessage || t("errorMessage")}</Text>
                <Button color="red" variant="light" onClick={() => router.push("/ai-color-analysis")}>{t("tryAgain")}</Button>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    );
  }

  // ============================================
  // RENDER: RESULTS
  // ============================================

  if (!analysisResult) return null;

  const undertoneStyle = UNDERTONE_STYLES[analysisResult.undertone] || UNDERTONE_STYLES.neutral;
  const contrastStyle = CONTRAST_STYLES[analysisResult.contrast] || CONTRAST_STYLES.medium;
  const macroSeasonLabel = t(`seasonNames.${analysisResult.season}`) || analysisResult.season;
  const depthLabel = analysisResult.depth ? DEPTH_LABELS[analysisResult.depth] || analysisResult.depth : "";
  const confidenceLabel =
    analysisResult.confidence >= 75 ? t("evidenceConfidenceHigh") : analysisResult.confidence >= 60 ? t("evidenceConfidenceGood") : null;
  const hasGuidedVerdict = typeof window !== "undefined" && !!sessionStorage.getItem("qaGuidedVerdict");

  return (
    <Box py={{ base: "md", md: "2rem" }} pb={{ base: "2rem", md: "4rem" }}>
      <style dangerouslySetInnerHTML={{ __html: shimmerStyles }} />
      <Container size="sm">
        <Stack gap="sm">

          {/* ============================================ */}
          {/* ABOVE THE FOLD - 4 elements only */}
          {/* ============================================ */}

          {/* 1. Kicker + Headline + subhead */}
          <Stack gap={2} align="center">
            <Text fz="xs" tt="uppercase" style={{ color: "#94A3B8", letterSpacing: 1.5 }}>
              {t("previewKicker")}
            </Text>
            <Title order={1} fz={{ base: "1.3rem", md: "1.5rem" }} ta="center" style={{ color: "#1E293B" }}>
              {t("readyHeadline")}
            </Title>
            <Text fz="xs" ta="center" style={{ color: "#64748B" }}>
              {t("readySubhead")}
            </Text>
          </Stack>

          {/* 2. Evidence list - proves the AI did real work on this photo */}
          <Paper p="sm" radius="md" style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <Stack gap={8}>
              <Text fz="xs" fw={700} tt="uppercase" style={{ color: "#1E293B", letterSpacing: 0.5 }}>
                {t("evidenceHeader")}
              </Text>

              <Group gap={8} align="flex-start" wrap="nowrap">
                <Box
                  ta="center"
                  py={2}
                  px={8}
                  style={{ backgroundColor: undertoneStyle.bg, borderRadius: 6, flexShrink: 0, minWidth: 64 }}
                >
                  <Text fz="xs" fw={700} style={{ color: undertoneStyle.text }}>{undertoneStyle.label}</Text>
                  <Text fz={9} style={{ color: undertoneStyle.text, opacity: 0.8 }}>{t("dimensionUndertoneLabel")}</Text>
                </Box>
                <Text fz="sm" style={{ color: "#475569" }}>
                  {t("evidenceUndertoneSentence", { undertone: analysisResult.undertone })}
                </Text>
              </Group>

              <Group gap={8} align="flex-start" wrap="nowrap">
                <Box
                  ta="center"
                  py={2}
                  px={8}
                  style={{ backgroundColor: contrastStyle.bg, borderRadius: 6, flexShrink: 0, minWidth: 64 }}
                >
                  <Text fz="xs" fw={700} style={{ color: contrastStyle.text }}>{contrastStyle.label}</Text>
                  <Text fz={9} style={{ color: contrastStyle.text, opacity: 0.8 }}>{t("dimensionContrastLabel")}</Text>
                </Box>
                <Text fz="sm" style={{ color: "#475569" }}>
                  {t("evidenceContrastSentence", { contrast: analysisResult.contrast })}
                </Text>
              </Group>

              {analysisResult.depth && (
                <Group gap={8} align="flex-start" wrap="nowrap">
                  <Box
                    ta="center"
                    py={2}
                    px={8}
                    style={{ backgroundColor: "#F3F4F6", borderRadius: 6, flexShrink: 0, minWidth: 64 }}
                  >
                    <Text fz="xs" fw={700} style={{ color: "#374151" }}>{depthLabel}</Text>
                    <Text fz={9} style={{ color: "#374151", opacity: 0.8 }}>{t("dimensionDepthLabel")}</Text>
                  </Box>
                  <Text fz="sm" style={{ color: "#475569" }}>
                    {t("evidenceDepthSentence", { depth: analysisResult.depth })}
                  </Text>
                </Group>
              )}

              <Group gap={8} align="flex-start" wrap="nowrap" pt={4} style={{ borderTop: "1px solid #E5E7EB" }} mt={2}>
                <Box
                  ta="center"
                  py={2}
                  px={8}
                  style={{ backgroundColor: "#F3F4F6", borderRadius: 6, flexShrink: 0, minWidth: 64 }}
                >
                  <Group gap={3} justify="center" wrap="nowrap">
                    <IconLock size={10} color="#6B7280" />
                    <Text fz={9} fw={700} style={{ color: "#6B7280" }}>••••</Text>
                  </Group>
                </Box>
                <Box>
                  <Text fz="sm" fw={600} style={{ color: "#1E293B" }}>
                    {t("evidenceExactSeasonLabel")} — •••• {macroSeasonLabel}
                  </Text>
                  <Text fz="xs" style={{ color: "#64748B" }}>
                    {t("evidenceExactSeasonBody")}
                  </Text>
                </Box>
              </Group>

              {confidenceLabel && (
                <Text fz="xs" fw={600} style={{ color: "#7C3AED" }}>
                  {confidenceLabel}
                </Text>
              )}
            </Stack>
          </Paper>

          {/* 3. Primary CTA */}
          <Box>
            <Title order={3} fz="1rem" ta="center" mb={6} style={{ color: "#1E293B" }}>
              {t("readyOfferTitle")}
            </Title>
            <Button
              size="lg"
              radius="md"
              fullWidth
              rightSection={<IconArrowRight size={18} />}
              onClick={handleCheckout}
              loading={checkoutLoading}
              styles={{
                root: {
                  height: 48,
                  background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(109, 40, 217, 0.3)",
                },
                label: { fontSize: "clamp(12px, 4vw, 14px)", fontWeight: 600 },
                section: { marginInlineStart: 6 },
              }}
            >
              {`${t("unlockCta")}${priceDisplay ? ` — ${priceDisplay}` : ""}`}
            </Button>
            {checkoutError && <Alert color="red" mt="sm" role="alert">{checkoutError}</Alert>}
            <Text fz={10} ta="center" mt={6} style={{ color: "#64748B" }}>
              {t("unlockCtaSub")}
            </Text>
            <Text
              fz={11}
              ta="center"
              mt={10}
              onClick={handleFreeEscape}
              style={{ color: "#94A3B8", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
            >
              {hasGuidedVerdict ? t("freeEscapeBack") : t("freeEscapeDefault")}
            </Text>
          </Box>

          {/* ============================================ */}
          {/* BELOW THE FOLD - Secondary content */}
          {/* ============================================ */}

          {/* 5. Bonus: Real draping previews (kept from original — these are visual gold) */}
          {drapingPreviews.length > 0 && (
            <Paper p="sm" radius="md" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
              <Group justify="space-between" align="center" mb={4}>
                <Text fz="xs" fw={600} style={{ color: "#0F172A" }}>{t("bonusTitle")}</Text>
                {drapingPreviews.length > 0 && (
                  <Badge size="xs" variant="light" color="gray">{t("aiGenerated")}</Badge>
                )}
              </Group>
              <Text fz={10} mb={8} style={{ color: "#64748B" }}>
                {t("bonusSubtext")}
              </Text>
              <Group gap={6} align="stretch">
                {[0, 1].map((index) => (
                  <Box key={index} style={{ flex: 1 }}>
                    <Box
                      style={{
                        aspectRatio: "3/4",
                        borderRadius: 6,
                        overflow: "hidden",
                        background: "#F1F5F9",
                        position: "relative",
                      }}
                    >
                      {drapingPreviews[index] ? (
                        <img
                          src={drapingPreviews[index]!}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box style={{ width: "100%", height: "100%", background: "#F1F5F9" }} />
                      )}
                    </Box>
                  </Box>
                ))}
                <Box style={{ flex: 1 }}>
                  <Box
                    style={{
                      aspectRatio: "3/4",
                      borderRadius: 6,
                      overflow: "hidden",
                      background: "#F8FAFC",
                      border: "1px dashed #CBD5E1",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 4,
                    }}
                    onClick={handleCheckout}
                  >
                    <IconLock size={16} color="#94A3B8" />
                    <Text fz="md" fw={700} mt={4} style={{ color: "#0F172A" }}>+142</Text>
                  </Box>
                </Box>
              </Group>
            </Paper>
          )}

          {/* 7. Locked guide preview - abstract bars */}
          <Paper p="md" radius="lg" style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E7EB", position: "relative", overflow: "hidden" }}>
            <Text fz="xs" fw={600} mb="sm" style={{ color: "#374151" }}>
              {STYLE_PROFILE_V1 ? t("spLockedGuideTitle") : t("lockedGuideTitle")}
            </Text>
            <Box style={{ position: "relative" }}>
              <Stack gap="xs">
                {(STYLE_PROFILE_V1
                  ? [
                      t("spRowSeason"),
                      t("spRowDrapes"),
                      t("spRowOutfits"),
                      t("spRowHair"),
                      t("spRowMakeup"),
                      t("spRowAvoid"),
                      t("spRowPdf"),
                    ]
                  : [
                      t("rowBestColors"),
                      t("rowAvoidColors"),
                      t("rowMakeupGuide"),
                      t("rowHairGuide"),
                      t("rowOutfitRules"),
                    ]
                ).map((item, i) => (
                  <Box
                    key={i}
                    py="xs"
                    px="sm"
                    style={{
                      backgroundColor: `hsl(${250 + i * 10}, 20%, ${88 + i * 2}%)`,
                      borderRadius: 6,
                      opacity: 0.7,
                    }}
                  >
                    <Text fz="sm" style={{ color: "#9CA3AF" }}>{item}</Text>
                  </Box>
                ))}
              </Stack>
              <Box
                pos="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(250, 250, 250, 0.7)",
                  backdropFilter: "blur(4px)",
                  gap: 8,
                }}
              >
                <IconLock size={28} color="#9CA3AF" />
                <Text fz="xs" fw={500} style={{ color: "#6B7280" }}>
                  {t("lockedGuideUnlock")}
                </Text>
              </Box>
            </Box>
          </Paper>
          <Text fz="xs" ta="center" style={{ color: "#9CA3AF" }}>
            {t("lockedGuideHint")}
          </Text>

          {/* Free digital drapes decline panel — the non-buyer's viral-loop
              exit, see PLAN-SUMMER-2026.md §5.5. Deliberately prominent, not
              fine print. Gated to decline signals only (Stripe cancel return
              or 60s dwell without checkout) so paying customers never see it. */}
          {FREE_DRAPES_V1 && showFreeDrapes && (
            <Paper
              radius="lg"
              p="lg"
              style={{
                background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
                border: "1px solid #DDD6FE",
              }}
            >
              <Stack gap="sm">
                {drapingPreviews.length > 0 && (
                  <Group gap={8}>
                    {drapingPreviews.slice(0, 2).map((preview, index) =>
                      preview ? (
                        <Box
                          key={index}
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 12,
                            overflow: "hidden",
                            boxShadow: "0 2px 8px rgba(109, 40, 217, 0.2)",
                          }}
                        >
                          <img
                            src={preview}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </Box>
                      ) : null
                    )}
                  </Group>
                )}
                <Text fw={700} fz="md" style={{ color: "#1E293B" }}>
                  {tFreeDrapes("resultTitle")}
                </Text>
                <Text fz="sm" style={{ color: "#475569" }}>
                  {tFreeDrapes("resultBody")}
                </Text>
                <Button
                  size="md"
                  fullWidth
                  onClick={() => {
                    trackCtaClick("fd_result_open");
                    router.push("/virtual-color-draping?src=result_decline");
                  }}
                  styles={{
                    root: {
                      background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                      border: "none",
                      boxShadow: "0 4px 14px rgba(109, 40, 217, 0.3)",
                    },
                    label: { fontSize: "clamp(12px, 4vw, 14px)", fontWeight: 600 },
                section: { marginInlineStart: 6 },
                  }}
                >
                  {tFreeDrapes("resultCta")}
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Email capture — the non-buyer fallback. Deliberately NOT passing
              season/resultLabel: the email must never leak the paid answer,
              only the free preview reading. */}
          <EmailResultsCapture
            source="ai-color-analysis"
            undertone={analysisResult.undertone}
            title={t("emailCaptureTitle")}
            subtitle={t("emailCaptureSub")}
          />
          <DrapeKitCard placement="prepurchase_result" />

        </Stack>
      </Container>

    </Box>
  );
}
